if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (this.submitButton) {
          this.submitButtonText = this.submitButton.querySelector('span');
          if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        }


        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Check max count on page load
        this.checkMaxCountOnLoad();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Get the product's max_count metafield if it exists
        const maxCount = parseInt(this.dataset.maxCount) || 0;
        const variantId = this.variantIdInput.value;

        // If max_count is set, check current cart before proceeding
        if (maxCount > 0) {
          this.checkMaxCountBeforeAdd(variantId, maxCount, evt);
          return;
        }

        // Continue with normal add to cart if no max_count
        this.addToCart(evt);
      }

      async checkMaxCountOnLoad() {
        // Get the product's max_count metafield if it exists
        const maxCount = parseInt(this.dataset.maxCount) || 0;
        if (maxCount <= 0) return;

        const variantId = this.variantIdInput.value;
        
        try {
          // Fetch current cart
          const response = await fetch('/cart.js');
          if (!response.ok) throw new Error('Failed to fetch cart');
          const cart = await response.json();

          // Check if product is already in cart
          const productInCart = cart.items.find(item => item.variant_id.toString() === variantId.toString());
          const currentQuantity = productInCart ? productInCart.quantity : 0;

          // If current quantity equals or exceeds max count, hide the add to cart button
          if (currentQuantity >= maxCount) {
            this.hideAddToCartButton(maxCount);
          }
        } catch (error) {
          console.error('Error checking max count on load:', error);
        }
      }

      hideAddToCartButton(maxCount) {
        // Hide the submit button
        if (this.submitButton) {
          this.submitButton.style.display = 'none';
        }
        
        // Show a message indicating the maximum has been reached
        const container = this.submitButton.parentNode;
        
        // Check if we already added the message
        let maxReachedMessage = container.querySelector('.max-count-reached-message');
        if (!maxReachedMessage) {
          maxReachedMessage = document.createElement('div');
          maxReachedMessage.classList.add('max-count-reached-message');
          maxReachedMessage.innerHTML = `<p>You've reached the maximum quantity (${maxCount}) for this product.</p>`;
          container.appendChild(maxReachedMessage);
        }
      }

      async checkMaxCountBeforeAdd(variantId, maxCount, evt) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        try {
          // Fetch current cart
          const response = await fetch('/cart.js');
          if (!response.ok) throw new Error('Failed to fetch cart');
          const cart = await response.json();

          // Check if product is already in cart
          const productInCart = cart.items.find(item => item.variant_id.toString() === variantId.toString());
          const currentQuantity = productInCart ? productInCart.quantity : 0;

          // Get quantity being added
          const quantityInput = this.querySelector('[name="quantity"]');
          const quantityToAdd = parseInt(quantityInput ? quantityInput.value : 1);

          // Check if adding would exceed max_count
          if (currentQuantity + quantityToAdd > maxCount) {
            // If current quantity already equals or exceeds max count, hide the button
            if (currentQuantity >= maxCount) {
              this.hideAddToCartButton(maxCount);
            } else {
              // Show error message about remaining quantity
              const remainingQty = maxCount - currentQuantity;
              this.handleErrorMessage(`You can only add ${remainingQty} more of this product (maximum: ${maxCount}).`);
            }

            // Reset button state
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.submitButton.removeAttribute('aria-disabled');

            // Dispatch event for cart-popup to show max count message
            const maxCountEvent = new CustomEvent('product:max-count-reached', {
              bubbles: true,
              detail: {
                productId: variantId,
                maxCount: maxCount,
                currentQuantity: currentQuantity
              }
            });
            document.dispatchEvent(maxCountEvent);

            return;
          }

          // If we're good to go, proceed with add to cart
          this.addToCart(evt);

        } catch (error) {
          console.error('Error checking max count:', error);
          // Fall back to normal add to cart if check fails
          this.addToCart(evt);
        }
      }

      addToCart(evt) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const currentVariantId = formData.get('id');
        
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            // Dispatch custom event for cart-popup
            const productAddedEvent = new CustomEvent('product:added-to-cart', {
              bubbles: true,
              detail: {
                productId: formData.get('id'),
                cartData: response
              }
            });
            document.dispatchEvent(productAddedEvent);

            const cartUpdatedEvent = new CustomEvent('cart:updated', {
              bubbles: true,
              detail: {
                cartData: response
              }
            });
            document.dispatchEvent(cartUpdatedEvent);

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }

            // After successful add, check if we need to update max count status for all products
            this.updateAllProductsMaxCountStatus(currentVariantId);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      // Add this new method to handle updating all product forms on the page
      async updateAllProductsMaxCountStatus(addedVariantId) {
        // Get all product forms on the page
        const allProductForms = document.querySelectorAll('product-form');
        if (!allProductForms.length) return;
        
        try {
          // Fetch current cart
          const response = await fetch('/cart.js');
          if (!response.ok) throw new Error('Failed to fetch cart');
          const cart = await response.json();
          
          // Update each product form
          allProductForms.forEach(form => {
            const variantId = form.variantIdInput.value;
            const maxCount = parseInt(form.dataset.maxCount) || 0;
            
            // Skip if no max count set
            if (maxCount <= 0) return;
            
            // Find this product in cart
            const productInCart = cart.items.find(item => item.variant_id.toString() === variantId.toString());
            const currentQuantity = productInCart ? productInCart.quantity : 0;
            
            // If current quantity equals or exceeds max count, hide the add to cart button
            if (currentQuantity >= maxCount) {
              this.showMaxCountMessage(form, maxCount);
            } else {
              // Otherwise, make sure the button is visible and message is removed
              this.clearMaxCountMessage(form);
            }
          });
        } catch (error) {
          console.error('Error updating products max count status:', error);
        }
      }

      // Helper method to clear max count message
      clearMaxCountMessage(form) {
        const submitButton = form.querySelector('[type="submit"]');
        if (!submitButton) return;

        const container = submitButton.parentNode;
        const maxReachedMessage = container.querySelector('.max-count-reached-message');

        if (maxReachedMessage) {
          maxReachedMessage.remove();
        }

        // Show the button if it was hidden
        if (submitButton) {
          submitButton.style.display = '';
        }
      }

      // Helper method to show max count message
      showMaxCountMessage(form, maxCount) {
        const submitButton = form.querySelector('[type="submit"]');
        if (!submitButton) return;

        // Hide the submit button
        submitButton.style.display = 'none';

        // Show a message indicating the maximum has been reached
        const container = submitButton.parentNode;

        // Check if we already added the message
        let maxReachedMessage = container.querySelector('.max-count-reached-message');
        if (!maxReachedMessage) {
          maxReachedMessage = document.createElement('div');
          maxReachedMessage.classList.add('max-count-reached-message');
          maxReachedMessage.innerHTML = `<p>You've reached the maximum quantity (${maxCount}) for this product.</p>`;
          container.appendChild(maxReachedMessage);
        }
      }
    }
  );
}
